import React, { Component } from "react";
import { Row, Col, Form, Accordion, Tab, Tabs, Badge, Card } from "react-bootstrap";

/**
 * GroupedFormRenderer - Reusable component that renders formConfig.fieldGroups[]
 * as accordion (default), tabs, or sections.
 *
 * Props:
 *   - fieldGroups:       Array of { groupId, groupName, targetTable, fields[] }
 *   - formValues:        { fieldName: value }
 *   - errors:            { fieldName: errorMessage }
 *   - onFieldChange:     (fieldName, value) => void
 *   - fieldVisibility:   { fieldName: boolean } — from InteractionEngine
 *   - fieldDisabled:     { fieldName: boolean } — from InteractionEngine
 *   - fieldOptions:      { fieldName: [{ value, label }] } — from InteractionEngine cascade
 *   - dataSourceOptions: { fieldName: [{ value, label }] } — from DataSourceManager
 *   - dataSourceManager: DataSourceManager instance (for loading state)
 *   - renderMode:        "accordion" | "tabs" | "sections" (default: "accordion")
 *   - readOnlyFields:    { fieldName: boolean } — from RBAC (ReactDataTableAdapter)
 */
export default class GroupedFormRenderer extends Component {
  // ─── Field Rendering ──────────────────────────────────────────────────

  renderField = (field) => {
    const {
      formValues = {},
      onFieldChange,
      fieldDisabled = {},
      fieldOptions = {},
      dataSourceOptions = {},
      dataSourceManager,
      readOnlyFields = {},
    } = this.props;

    const value = formValues[field.fieldName] || "";
    const inputType = (field.inputType || "text").toLowerCase();
    const hasDataSource = !!field.dataSource;
    const dsOptions = dataSourceOptions[field.fieldName] || [];
    const interactionOptions = fieldOptions[field.fieldName];
    const isDisabled = fieldDisabled[field.fieldName] || false;
    const isReadOnly = readOnlyFields[field.fieldName] || field.readOnly || false;

    // Select (API-backed or static)
    if (hasDataSource || inputType === "select") {
      const options = interactionOptions || (hasDataSource ? dsOptions : (field.options || []));
      const isLoading = hasDataSource && dataSourceManager && dataSourceManager.isLoading(field.dataSource);

      return (
        <Form.Select
          value={value}
          onChange={(e) => onFieldChange(field.fieldName, e.target.value)}
          required={field.required}
          disabled={isLoading || isDisabled || isReadOnly}
        >
          <option value="">
            {isLoading ? "Loading..." : `Select ${field.displayName || field.fieldName}`}
          </option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Form.Select>
      );
    }

    switch (inputType) {
      case "number":
        return (
          <Form.Control
            type="number"
            value={value}
            onChange={(e) => onFieldChange(field.fieldName, e.target.value)}
            placeholder={`Enter ${field.displayName || field.fieldName}`}
            required={field.required}
            disabled={isDisabled || isReadOnly}
          />
        );
      case "date":
        return (
          <Form.Control
            type="date"
            value={value}
            onChange={(e) => onFieldChange(field.fieldName, e.target.value)}
            required={field.required}
            disabled={isDisabled || isReadOnly}
          />
        );
      case "textarea":
        return (
          <Form.Control
            as="textarea"
            rows={field.rows || 3}
            value={value}
            onChange={(e) => onFieldChange(field.fieldName, e.target.value)}
            placeholder={`Enter ${field.displayName || field.fieldName}`}
            required={field.required}
            disabled={isDisabled || isReadOnly}
          />
        );
      case "email":
        return (
          <Form.Control
            type="email"
            value={value}
            onChange={(e) => onFieldChange(field.fieldName, e.target.value)}
            placeholder={`Enter ${field.displayName || field.fieldName}`}
            required={field.required}
            disabled={isDisabled || isReadOnly}
          />
        );
      case "tel":
        return (
          <Form.Control
            type="tel"
            value={value}
            onChange={(e) => onFieldChange(field.fieldName, e.target.value)}
            placeholder={`Enter ${field.displayName || field.fieldName}`}
            required={field.required}
            disabled={isDisabled || isReadOnly}
          />
        );
      case "multiselect": {
        const msOptions = interactionOptions || (hasDataSource ? dsOptions : (field.options || []));
        return (
          <Form.Select
            multiple
            value={value ? (Array.isArray(value) ? value : [value]) : []}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions, (opt) => opt.value);
              onFieldChange(field.fieldName, selected);
            }}
            required={field.required}
            disabled={isDisabled || isReadOnly}
          >
            {msOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        );
      }
      default:
        return (
          <Form.Control
            type="text"
            value={value}
            onChange={(e) => onFieldChange(field.fieldName, e.target.value)}
            placeholder={`Enter ${field.displayName || field.fieldName}`}
            required={field.required}
            disabled={isDisabled || isReadOnly}
          />
        );
    }
  };

  // ─── Render a Single Group's Fields ───────────────────────────────────

  renderGroupFields = (group) => {
    const { fieldVisibility = {}, errors = {} } = this.props;
    const fields = group.fields || [];

    return fields.map((field) => {
      if (fieldVisibility[field.fieldName] === false) {
        return null;
      }

      return (
        <Row key={field.fieldName} className="mb-2">
          <Col lg="4" md="4">
            <Form.Label style={{ paddingTop: "4px" }}>
              {field.displayName || field.fieldName}
              {field.required && <span className="text-danger">*</span>}
            </Form.Label>
          </Col>
          <Col lg="8" md="8">
            {this.renderField(field)}
            {errors[field.fieldName] && (
              <div className="text-danger" style={{ fontSize: "0.875rem" }}>
                {errors[field.fieldName]}
              </div>
            )}
          </Col>
        </Row>
      );
    });
  };

  // ─── Group Header Badge ───────────────────────────────────────────────

  renderGroupHeader = (group) => {
    return (
      <span>
        {group.groupName || group.groupId}
        {group.targetTable && (
          <Badge bg="secondary" className="ms-2" style={{ fontSize: "0.7em", fontWeight: "normal" }}>
            {group.targetTable}
          </Badge>
        )}
      </span>
    );
  };

  // ─── Accordion Mode ───────────────────────────────────────────────────

  renderAccordion = (groups) => {
    // Open all groups by default
    const defaultKeys = groups.map((_, i) => String(i));

    return (
      <Accordion defaultActiveKey={defaultKeys} alwaysOpen>
        {groups.map((group, index) => (
          <Accordion.Item eventKey={String(index)} key={group.groupId || index}>
            <Accordion.Header>
              {this.renderGroupHeader(group)}
            </Accordion.Header>
            <Accordion.Body>
              {this.renderGroupFields(group)}
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    );
  };

  // ─── Tabs Mode ────────────────────────────────────────────────────────

  renderTabs = (groups) => {
    return (
      <Tabs defaultActiveKey={groups[0]?.groupId || "0"} className="mb-3">
        {groups.map((group) => (
          <Tab
            eventKey={group.groupId}
            key={group.groupId}
            title={this.renderGroupHeader(group)}
          >
            <div className="pt-2">
              {this.renderGroupFields(group)}
            </div>
          </Tab>
        ))}
      </Tabs>
    );
  };

  // ─── Sections Mode ────────────────────────────────────────────────────

  renderSections = (groups) => {
    return groups.map((group, index) => (
      <Card key={group.groupId || index} className={index > 0 ? "mt-3" : ""}>
        <Card.Header className="py-2">
          <strong>{this.renderGroupHeader(group)}</strong>
        </Card.Header>
        <Card.Body>
          {this.renderGroupFields(group)}
        </Card.Body>
      </Card>
    ));
  };

  // ─── Main Render ──────────────────────────────────────────────────────

  render() {
    const { fieldGroups = [], renderMode = "accordion" } = this.props;

    if (!fieldGroups || fieldGroups.length === 0) {
      return <div className="text-muted p-2">No field groups configured.</div>;
    }

    // Single group — render flat without grouping wrapper
    if (fieldGroups.length === 1) {
      return <div>{this.renderGroupFields(fieldGroups[0])}</div>;
    }

    // Multiple groups — render based on mode
    switch (renderMode) {
      case "tabs":
        return this.renderTabs(fieldGroups);
      case "sections":
        return this.renderSections(fieldGroups);
      case "accordion":
      default:
        return this.renderAccordion(fieldGroups);
    }
  }
}
